// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { OffsetOptions, useFloatingTree } from "@floating-ui/react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import FancyScroll from "@sc/App/Components/Common/FancyScroll";
import FloutingUiMenu from "@sc/App/Components/Menu/DropdownMenu";
import { color, ellipsis, xClassNames } from "@sc/XDL/Utilities";
import { styledScrollbar } from "@sc/XDL/Utilities/scroll";
import React, { useCallback, useMemo, useRef, useState } from "react";
import styled, { css, ThemeProps, useTheme } from "styled-components";

export type MenuVariants = "primary" | "secondary";

export const DEFAULT_BOUNDARY_SELECTOR = "header [data-test-selector='main-navigation'] .container";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
        // XNG-Changes
        fancyScrollStyle?: InjectableCss;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string }>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** XNG-Changes: tippy trigger prop */
        tippyTrigger?: string;
        /** XNG-Changes: To prevent Menu close by click on trigger */
        hideOnClick?: boolean;
        /** XNG-Changes */
        headingLink?: { url: string; title?: string };
        /** XNG-Change: tippy touch prop */
        tippyTouch?: boolean | "hold" | ["hold", number];
        /** XNG-Change: use arrow triggered children tippy menu */
        useArrowTrigger?: boolean;
        /**
         * XNG-Changes
         * Choose 'Primary' when the subMenu needs to match the parent menu height.
         */
        menuVariant?: MenuVariants;
        referenceIndex?: number;
        offset?: OffsetOptions;
        menuBoundary?: string;
        /** XNG-Changes: Callback for menu item clicks */
        onMenuItemsClick?: (url: string, label?: string) => void;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

const MenuStyle = styled.div<MenuStyleProps>`
    background: ${getColor("common.background")};
    width: ${getProp("width", 330)}px;
    max-width: 25vw;
    list-style: none;
    height: 100%;
    min-height: auto;
    max-height: calc(calc(100vh - 108px) * 0.7);
    margin: 0;
    padding: 13px 0 0 5px;
    overflow: auto;
    ${styledScrollbar(4)}

    // Style the outer div wrapper with role="menuitem" and class="MenuItem"
    div.MenuItem[role="menuitem"] {
        &:focus-visible {
            outline: none;
        }
    }

    ${injectCss}
`;

type MenuWrapperProps = { isOpen?: true } & InjectableCss;

// XNG-Change
const MenuWrapper = styled.nav<MenuWrapperProps>`
    .trigger-item {
        padding: 4px 8px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }
    &[data-open] {
        .trigger-item {
            position: relative;
            ${TypographyStyle} {
                color: ${color("text.main")};
            }
            ${IconWrapper} {
                rotate: 180deg;
                color: ${color("text.main")};
            }
            background-color: ${color("common.ui12")};
        }
    }

    ${injectCss}
`;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        // onClick: () => void;
    };

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 6px 8px;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    width: 100%;
    position: relative;
    font-size: 14px;
    line-height: 22px;
    ${ellipsis(2)}
`;

const menuItemColorStyle = {
    default: {
        background: "transparent",
        text: "text.main",
        icon: "text.link",
    },
    active: {
        background: "common.ui12",
        text: "text.main",
        icon: "common.ui9",
    },
};

const IconMemoStyled = styled(IconMemo)<IconPresentationProps>`
    top: 7px;
    height: 20px;
    width: 20px;
    color: ${getColor("primary.main")};
`;

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 4px;

    & + [data-tippy-root] {
        &,
        & > div {
            height: 100%;
        }
    }

    &:focus {
        outline: none;
    }

    ${StyledButton} {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }

    ${IconWrapper} {
        color: ${color(menuItemColorStyle.default.icon)};
        width: 24px;
        height: 24px;
        margin-left: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        position: static;
        transform: none;
        padding: 0 0 0 8px;
        flex-shrink: 0;
    }

    &:hover,
    &[data-open] {
        background: ${color(menuItemColorStyle.active.background)};
        ${IconWrapper} {
            color: ${color(menuItemColorStyle.active.icon)};
        }
        ${MenuItemText} {
            color: ${color(menuItemColorStyle.active.text)};
        }
    }

    ${injectCss}
`;

// XNG-Change
const ArrowTippyIconWrapperStyle = styled.div`
    position: relative;
    cursor: pointer;
    flex: 1;
    && span {
        width: 100%;
        height: 24px;
        justify-content: flex-end;
        svg {
            width: 16px;
        }
    }
`;

// XNG-Change
const ArrowTippyStyle = styled.div<MenuItemStyleProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 4px;

    &:focus {
        outline: none;
    }

    ${IconWrapper} {
        color: ${color(menuItemColorStyle.default.icon)};
        width: 24px;
        height: 24px;
        margin-left: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        position: static;
        transform: none;
        padding: 0 0 0 8px;
        flex-shrink: 0;
    }

    &:hover,
    &[data-open] {
        background: ${color(menuItemColorStyle.active.background)};
        ${IconWrapper} {
            color: ${color(menuItemColorStyle.active.icon)};
        }
        ${MenuItemText} {
            color: ${color(menuItemColorStyle.active.text)};
        }
    }

    ${injectCss}
`;

const DEFAULT_MENU_WIDTH = 333;

// Helper component to emit tree click event when menu items are clicked
const MenuItemClickHandler: React.FC<{ children: React.ReactNode; onClick?: () => void }> = React.memo(
    ({ children, onClick }) => {
        const tree = useFloatingTree();

        const handleClick = useCallback(
            (e: React.MouseEvent) => {
                // Emit tree click event to close all menus
                if (tree) {
                    tree.events.emit("click");
                }
                // Call the original onClick handler
                onClick?.();
            },
            [tree, onClick],
        );

        const child = React.Children.only(children) as React.ReactElement<any>;
        const childProps = child.props as any;
        return React.cloneElement(child, {
            ...childProps,
            onClick: (e: React.MouseEvent) => {
                handleClick(e);
                childProps.onClick?.(e);
            },
        });
    },
);

MenuItemClickHandler.displayName = "MenuItemClickHandler";

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
const Menu: React.FC<MenuProps> = props => {
    const {
        maxDepth = 3,
        width = 330,
        tippyTrigger = "mouseenter focusin",
        hideOnClick = false,
        tippyTouch = ["hold", 250],
        useArrowTrigger = false,
        menuItems,
        menuTrigger,
        isOpen,
        mergeCss,
        menuVariant,
        offset,
        referenceIndex,
        menuBoundary,
        displayChangeCustomerLink,
        onMenuItemsClick,
        ...otherProps
    } = props;

    const theme = useTheme() as BaseTheme;
    const [shouldRender, setShouldRender] = useState(true);
    const triggerRef = useRef<HTMLElement>(null);

    const closeMenu = useCallback(() => {
        // Emit a tree click event to close all menus
        // The Floating UI tree listens for this event and closes all menus
        // This is the same event that MenuItem component emits
        const event = new CustomEvent("floating-ui-tree-click", {
            bubbles: true,
            cancelable: true,
        });
        document.dispatchEvent(event);

        // Also try to find and emit the tree click event directly
        // Find any element in the Floating UI tree and trigger the event
        const floatingNode = document.querySelector("[data-floating-ui-context]");
        if (floatingNode) {
            // Try to access the tree from the element
            const tree = (floatingNode as any).__floatingTree;
            if (tree && tree.events) {
                tree.events.emit("click");
            }
        }

        // Fallback: Close by triggering mouseleave on the root menu trigger
        const rootMenuTrigger = document.querySelector('.RootMenu[role="button"], nav[aria-labelledby] .trigger-item');
        if (rootMenuTrigger) {
            let tooltipWrapper = rootMenuTrigger.closest("span[data-state]");
            if (!tooltipWrapper) {
                tooltipWrapper = rootMenuTrigger.parentElement?.closest("span[data-state]") || null;
            }

            if (tooltipWrapper instanceof HTMLElement) {
                const mouseLeaveEvent = new MouseEvent("mouseleave", {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                });
                tooltipWrapper.dispatchEvent(mouseLeaveEvent);
            }
            if (rootMenuTrigger instanceof HTMLElement) {
                rootMenuTrigger.blur();
            }
        }

        setTimeout(() => {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        }, 100);
    }, []);

    const closeMenuWithoutChildren = useCallback(
        (hasMenuChildren?: boolean) => {
            // Close the menu when clicking on any menu item
            // This ensures the menu closes on click, but can still be opened again on hover
            closeMenu();

            // This is for high resolution mobile users.
            // So that the menu closes on nav items without children
            if (!hasMenuChildren) {
                setShouldRender(false);
                // Use requestAnimationFrame for better performance than setTimeout
                requestAnimationFrame(() => {
                    setShouldRender(true);
                });
            }
        },
        [closeMenu],
    );

    // Memoize applyPropBuilder result
    const { applyProp, spreadProps, applyStyledProp } = useMemo(
        () => applyPropBuilder({ ...otherProps, theme: theme as BaseTheme }, { component: "menu" }),
        [otherProps, theme],
    );

    const resolvedMergeCss = useMemo(
        () => mergeCss ?? (theme as BaseTheme)?.menu?.defaultProps?.mergeCss,
        [mergeCss, theme],
    );

    // Memoize filtered menu items
    const filteredMenuItems = useMemo(() => menuItems?.filter(item => !item.excludeFromNavigation) || [], [menuItems]);

    // Memoize CSS overrides - spreadProps returns an object, not a function
    const cssOverrides = useMemo(() => {
        const props = spreadProps("cssOverrides" as any);
        return props;
    }, [spreadProps]);

    // Memoized menu item component
    const MenuItemComponent = React.memo<{
        item: MappedLink;
        index: number;
        parentDepth: number;
        currentDepth: number;
        hasChildren: boolean;
        maxDepth: number;
        level: number;
        width: number;
        useArrowTrigger: boolean;
        displayChangeCustomerLink?: boolean;
        shouldRender: boolean;
        onMenuItemsClick?: (url: string, label?: string) => void;
        applyProp: <T>(name: "theme" | string[], fallback?: T) => any;
        applyStyledProp: (path: string[], mergeCss?: boolean) => any;
        resolvedMergeCss?: boolean;
        closeMenu: () => void;
        closeMenuWithoutChildren: (hasChildren?: boolean) => void;
    }>(
        ({
            item,
            index,
            parentDepth,
            currentDepth,
            hasChildren,
            maxDepth,
            level,
            width,
            useArrowTrigger,
            displayChangeCustomerLink,
            shouldRender,
            onMenuItemsClick,
            applyProp,
            applyStyledProp,
            resolvedMergeCss,
            closeMenu,
            closeMenuWithoutChildren,
        }) => {
            const handleMenuItemClick = useCallback(() => {
                closeMenuWithoutChildren(hasChildren);
            }, [hasChildren, closeMenuWithoutChildren]);

            const handleClickableClick = useCallback(() => {
                if (onMenuItemsClick && item.url) {
                    onMenuItemsClick(item.url, item.title);
                }
            }, [onMenuItemsClick, item.url, item.title]);

            const menuItem = (
                <MenuItemStyle
                    width={width}
                    css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                    key={`${item.title}_${index}`}
                    className={xClassNames.append(["MenuItem", hasChildren ? "has-children" : ""])}
                    data-test-selector="menuItem"
                    data-depth={currentDepth}
                    onClick={handleMenuItemClick}
                >
                    <MenuItemClickHandler onClick={closeMenu}>
                        <Clickable
                            aria-label={item.title}
                            href={item.url}
                            target={item.openInNewWindow ? "_blank" : ""}
                            onClick={handleClickableClick}
                            {...applyProp(["menuItemClickableProps"])}
                        >
                            <MenuItemText
                                hasChildren={hasChildren}
                                {...applyProp(["menuItemTypographyProps"])}
                                weight="normal"
                            >
                                {item.title}
                            </MenuItemText>
                            {hasChildren && <IconMemo {...applyProp(["moreIconProps"])} />}
                        </Clickable>
                    </MenuItemClickHandler>
                </MenuItemStyle>
            );

            if (!hasChildren) {
                return menuItem;
            }

            if (useArrowTrigger) {
                const parentMenuTriggerItemRef = useRef<HTMLLIElement>(null);
                const arrowTippy = (
                    <ArrowTippyStyle>
                        <MenuItemStyle
                            width={width}
                            css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                            key={`${item.title}_${index}`}
                            className={xClassNames.append(["MenuItem", hasChildren ? "has-children" : ""])}
                            data-test-selector="menuItem"
                            data-depth={currentDepth}
                            onClick={handleMenuItemClick}
                            ref={parentMenuTriggerItemRef}
                        >
                            <Clickable
                                aria-label={item.title}
                                href={item.url}
                                onClick={() => {
                                    handleClickableClick();
                                    closeMenu();
                                }}
                                {...applyProp(["menuItemClickableProps"])}
                            >
                                <MenuItemText
                                    hasChildren={hasChildren}
                                    {...applyProp(["menuItemTypographyProps"])}
                                    weight="normal"
                                >
                                    {item.title}
                                </MenuItemText>
                            </Clickable>
                        </MenuItemStyle>
                        <FloutingUiMenu
                            menuTrigger={
                                <ArrowTippyIconWrapperStyle>
                                    <IconMemo {...applyProp(["moreIconProps"])} />
                                </ArrowTippyIconWrapperStyle>
                            }
                            placement="right-start"
                            offset={{ mainAxis: 0 }}
                            delay={0}
                        >
                            <MenuItemsList
                                parentDepth={maxDepth}
                                childMenuItems={item.children!}
                                currentDepth={currentDepth + 1}
                                level={2}
                                width={width}
                                displayChangeCustomerLink={displayChangeCustomerLink}
                                shouldRender={shouldRender}
                                useArrowTrigger={useArrowTrigger}
                                onMenuItemsClick={onMenuItemsClick}
                                applyProp={applyProp}
                                applyStyledProp={applyStyledProp}
                                resolvedMergeCss={resolvedMergeCss}
                                closeMenu={closeMenu}
                                closeMenuWithoutChildren={closeMenuWithoutChildren}
                            />
                        </FloutingUiMenu>
                    </ArrowTippyStyle>
                );
                return arrowTippy;
            }

            return (
                <FloutingUiMenu
                    menuTrigger={menuItem}
                    placement="right-start"
                    offset={{ mainAxis: 0 }}
                    delay={0}
                    overlay={true}
                >
                    <MenuItemsList
                        parentDepth={maxDepth}
                        childMenuItems={item.children!}
                        currentDepth={currentDepth + 1}
                        level={2}
                        width={width}
                        displayChangeCustomerLink={displayChangeCustomerLink}
                        shouldRender={shouldRender}
                        useArrowTrigger={useArrowTrigger}
                        onMenuItemsClick={onMenuItemsClick}
                        applyProp={applyProp}
                        applyStyledProp={applyStyledProp}
                        resolvedMergeCss={resolvedMergeCss}
                        closeMenu={closeMenu}
                        closeMenuWithoutChildren={closeMenuWithoutChildren}
                    />
                </FloutingUiMenu>
            );
        },
        (prevProps, nextProps) => {
            return (
                prevProps.item.title === nextProps.item.title &&
                prevProps.item.url === nextProps.item.url &&
                prevProps.hasChildren === nextProps.hasChildren &&
                prevProps.currentDepth === nextProps.currentDepth &&
                prevProps.index === nextProps.index &&
                prevProps.width === nextProps.width &&
                prevProps.useArrowTrigger === nextProps.useArrowTrigger &&
                prevProps.displayChangeCustomerLink === nextProps.displayChangeCustomerLink &&
                prevProps.shouldRender === nextProps.shouldRender
            );
        },
    );
    MenuItemComponent.displayName = "MenuItemComponent";

    // Memoized menu items list component
    const MenuItemsList = React.memo<{
        parentDepth: number;
        childMenuItems: MappedLink[];
        currentDepth: number;
        level: number;
        width: number;
        displayChangeCustomerLink?: boolean;
        shouldRender: boolean;
        useArrowTrigger: boolean;
        onMenuItemsClick?: (url: string, label?: string) => void;
        applyProp: <T>(name: "theme" | string[], fallback?: T) => any;
        applyStyledProp: (path: string[], mergeCss?: boolean) => any;
        resolvedMergeCss?: boolean;
        closeMenu: () => void;
        closeMenuWithoutChildren: (hasChildren?: boolean) => void;
    }>(
        ({
            parentDepth,
            childMenuItems,
            currentDepth,
            level,
            width,
            displayChangeCustomerLink,
            shouldRender,
            useArrowTrigger,
            onMenuItemsClick,
            applyProp,
            applyStyledProp,
            resolvedMergeCss,
            closeMenu,
            closeMenuWithoutChildren,
        }) => {
            const parentMenuRef = useRef<HTMLDivElement>(null);

            const filteredItems = useMemo(
                () => childMenuItems?.filter(item => !item.excludeFromNavigation) || [],
                [childMenuItems],
            );

            return (
                <FancyScroll
                    refElement={parentMenuRef}
                    showShadow={false}
                    extendedStyles={applyStyledProp(["cssOverrides", "fancyScrollStyle"], resolvedMergeCss)}
                >
                    <MenuStyle
                        width={width}
                        ref={parentMenuRef}
                        className={xClassNames.append(["MenuStyle", level === 1 && "level1", level === 2 && "level2"])}
                        data-depth={currentDepth}
                        css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                    >
                        {filteredItems.map((item, index) => {
                            const itemMaxDepth = item.maxDepth || parentDepth;
                            const hasChildren = Boolean(
                                item.children?.some(o => !o.excludeFromNavigation) && currentDepth < itemMaxDepth,
                            );

                            if (
                                (item.type === "ChangeCustomerPage" && displayChangeCustomerLink === false) ||
                                !shouldRender
                            ) {
                                return null;
                            }

                            return (
                                <MenuItemComponent
                                    key={`${item.title}_${index}`}
                                    item={item}
                                    index={index}
                                    parentDepth={parentDepth}
                                    currentDepth={currentDepth}
                                    hasChildren={hasChildren}
                                    maxDepth={itemMaxDepth}
                                    level={level}
                                    width={width}
                                    useArrowTrigger={useArrowTrigger}
                                    displayChangeCustomerLink={displayChangeCustomerLink ?? false}
                                    shouldRender={shouldRender}
                                    onMenuItemsClick={onMenuItemsClick}
                                    applyProp={applyProp}
                                    applyStyledProp={applyStyledProp}
                                    resolvedMergeCss={resolvedMergeCss}
                                    closeMenu={closeMenu}
                                    closeMenuWithoutChildren={closeMenuWithoutChildren}
                                />
                            );
                        })}
                    </MenuStyle>
                </FancyScroll>
            );
        },
        (prevProps, nextProps) => {
            return (
                prevProps.parentDepth === nextProps.parentDepth &&
                prevProps.currentDepth === nextProps.currentDepth &&
                prevProps.level === nextProps.level &&
                prevProps.width === nextProps.width &&
                prevProps.displayChangeCustomerLink === nextProps.displayChangeCustomerLink &&
                prevProps.shouldRender === nextProps.shouldRender &&
                prevProps.useArrowTrigger === nextProps.useArrowTrigger &&
                prevProps.childMenuItems === nextProps.childMenuItems &&
                prevProps.onMenuItemsClick === nextProps.onMenuItemsClick
            );
        },
    );
    MenuItemsList.displayName = "MenuItemsList";

    // Memoize description ID
    const descriptionId = useMemo(() => otherProps.descriptionId || uniqueId(), [otherProps.descriptionId]);

    // Memoize boundary selector
    const boundarySelector = useMemo(() => {
        if (typeof document === "undefined") {
            return "clippingAncestors";
        }
        return document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body;
    }, []);

    // Memoize cloned menu trigger
    const clonedMenuTrigger = useMemo(
        () =>
            React.cloneElement(menuTrigger, {
                id: descriptionId.split(" ").join("_"),
                className: `trigger trigger-item`,
            }),
        [menuTrigger, descriptionId],
    );

    return (
        <>
            <FloutingUiMenu
                menuTrigger={
                    <MenuWrapper
                        css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                        {...{ "aria-labelledby": descriptionId }}
                    >
                        {clonedMenuTrigger}
                    </MenuWrapper>
                }
                shift={{
                    boundary: boundarySelector,
                    padding: 16,
                }}
                placement="bottom-start"
                offset={{ mainAxis: 9 }}
                delay={0}
                overlay={true}
            >
                <StyledWrapper
                    css={css`
                        display: flex;
                        span[class^="ScrollIndicator"] {
                            max-width: ${DEFAULT_MENU_WIDTH - 6.5}px;
                        }
                    `}
                >
                    <StyledWrapper
                        css={css`
                            flex-shrink: 0;
                        `}
                    >
                        <MenuItemsList
                            parentDepth={maxDepth}
                            childMenuItems={filteredMenuItems}
                            currentDepth={1}
                            level={2}
                            width={width}
                            displayChangeCustomerLink={displayChangeCustomerLink}
                            shouldRender={shouldRender}
                            useArrowTrigger={useArrowTrigger}
                            onMenuItemsClick={onMenuItemsClick}
                            applyProp={applyProp}
                            applyStyledProp={applyStyledProp}
                            resolvedMergeCss={resolvedMergeCss}
                            closeMenu={closeMenu}
                            closeMenuWithoutChildren={closeMenuWithoutChildren}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </FloutingUiMenu>
            <span ref={triggerRef} tabIndex={-1} />
        </>
    );
};

/** @component */
export default React.memo(Menu);
export { MenuItemText };
